import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Brand theme context provider and hook.
 * Makes derived brand tokens available to all React components in the widget.
 */
import { createContext, useContext, useMemo } from 'react';
import { deriveBrandTokens } from '../styles/brandUtils';
// =============================================================================
// CONTEXT
// =============================================================================
/**
 * Context for brand theming.
 * Provides derived brand tokens to all child components.
 */
const BrandThemeContext = createContext(null);
/**
 * Provider component that makes brand tokens available to all children.
 * Tokens are pre-derived by BaseWidget to avoid redundant computation.
 *
 * @example
 * ```tsx
 * <BrandThemeProvider tokens={derivedTokens} rawConfig={originalConfig}>
 *   <App />
 * </BrandThemeProvider>
 * ```
 */
export const BrandThemeProvider = ({ tokens, rawConfig, children, }) => {
    const value = useMemo(() => ({
        brand: tokens,
        rawConfig: rawConfig || {},
    }), [tokens, rawConfig]);
    return (_jsx(BrandThemeContext.Provider, { value: value, children: children }));
};
// =============================================================================
// HOOKS
// =============================================================================
/**
 * Default brand tokens used as fallback when context is not available.
 * This can happen during HMR in development mode.
 */
const defaultBrandTokens = deriveBrandTokens({});
/**
 * Hook to access derived brand tokens.
 *
 * Must be used within a BrandThemeProvider in production.
 * In development, falls back to default tokens during HMR reloads.
 *
 * @returns Fully resolved brand tokens
 * @throws Error if used outside BrandThemeProvider (production only)
 *
 * @example
 * ```tsx
 * const brand = useBrandTheme();
 * return <div style={{ color: brand.primaryColor }}>Styled content</div>;
 * ```
 */
export const useBrandTheme = () => {
    const context = useContext(BrandThemeContext);
    if (!context) {
        // In development, HMR can cause components to render before providers are ready
        // Fall back to default tokens to prevent crashes during HMR
        if (process.env.NODE_ENV === 'development') {
            return defaultBrandTokens;
        }
        throw new Error('useBrandTheme must be used within a BrandThemeProvider. ' +
            'Ensure your component is wrapped in <BrandThemeProvider>.');
    }
    return context.brand;
};
/**
 * Hook to access full brand context including raw config.
 *
 * Useful for debugging or when you need to know what the host originally passed.
 *
 * @returns Full brand context value
 * @throws Error if used outside BrandThemeProvider
 */
export const useBrandThemeContext = () => {
    const context = useContext(BrandThemeContext);
    if (!context) {
        throw new Error('useBrandThemeContext must be used within a BrandThemeProvider. ' +
            'Ensure your component is wrapped in <BrandThemeProvider>.');
    }
    return context;
};
//# sourceMappingURL=BrandThemeContext.js.map